import type { IGlobalTab, IInnerTab } from '@carboncopyco/types/desktop';
import { AppEventsEnum } from '../../shared/enums/AppEventsEnum';
import rendererService from '../services/RendererService';
import { useTabManager } from '../services/TabManager';
import useElectronListener from './useElectronListener';

const useInnerViewLoaded = (
  setGlobalTabs: (
    _globalTabs: IGlobalTab[] | ((prevTabs: IGlobalTab[]) => IGlobalTab[])
  ) => void
) => {
  const { updateTabProperty, getActiveGlobalTab, navigateGlobalTab } =
    useTabManager();
  const getTabsWitInnerViewLoadedUpdates = (
    oldGlobalTabs: IGlobalTab[],
    eventArgs: {
      senderGlobalTab: IGlobalTab;
      senderInnerTab: IInnerTab;
      isLoaded: boolean;
    }
  ) => {
    const { senderGlobalTab, senderInnerTab, isLoaded } = eventArgs;

    return oldGlobalTabs.map((oldGlobalTab) => {
      const isTabToUpdate = senderGlobalTab.id === oldGlobalTab.id;
      if (!isTabToUpdate) return oldGlobalTab;

      const updatedInnerTabs = oldGlobalTab.innerTabs.map((oldInnerTab) => {
        const isInnerTabToUpdate = oldInnerTab.id === senderInnerTab.id;
        return isInnerTabToUpdate
          ? {
              ...oldInnerTab,
              isInnerViewLoaded: isLoaded,
            }
          : oldInnerTab;
      });

      return {
        ...oldGlobalTab,
        innerTabs: updatedInnerTabs,
      };
    });
  };

  interface InnerViewLoadedArgs {
    globalTab: IGlobalTab;
    innerTab: IInnerTab;
    loaded: boolean;
  }

  const handleInnerViewLoaded = (data: unknown) => {
    const { globalTab, innerTab, loaded } = data as InnerViewLoadedArgs;
    const senderGlobalTab = globalTab as IGlobalTab;
    const senderInnerTab = innerTab as IInnerTab;
    const isLoaded = loaded as boolean;
    setGlobalTabs((oldGlobalTabs: IGlobalTab[]) => {
      return getTabsWitInnerViewLoadedUpdates(oldGlobalTabs, {
        senderGlobalTab,
        senderInnerTab,
        isLoaded,
      });
    });

    if (getActiveGlobalTab()?.id === senderGlobalTab.id) {
      senderGlobalTab.innerTabs[0].isInnerViewLoaded = isLoaded;
      const hasOpenModals = rendererService.getHasOpenModals();
      navigateGlobalTab(senderGlobalTab);
      // if modal was open before navigating to the tab, focus the app to see modal again
      if (hasOpenModals) {
        rendererService.focusApp();
      }
    }
  };

  useElectronListener(AppEventsEnum.INNER_VIEW_LOADED, handleInnerViewLoaded);
  useElectronListener(
    AppEventsEnum.MAIN_TO_APP_VIEW_SET_TAB_TITLE,
    (senderTab, tabTitle) => {
      const tab = senderTab as IGlobalTab;
      if (tab.group?.mergingOptions?.allowed) return;
      updateTabProperty(senderTab as IGlobalTab, 'title', tabTitle as string);
    }
  );
  useElectronListener(
    AppEventsEnum.MAIN_TO_APP_VIEW_SET_TAB_FAVICON,
    (senderTab, tabTitle) => {
      const tab = senderTab as IGlobalTab;
      if (tab.group?.mergingOptions?.allowed) return;
      updateTabProperty(senderTab as IGlobalTab, 'favicon', tabTitle as string);
    }
  );
  useElectronListener(
    AppEventsEnum.MAIN_TO_APP_VIEW_SET_TAB_URL,
    (senderTab, url) => {
      updateTabProperty(senderTab as IGlobalTab, 'url', url as string);
    }
  );
};

export default useInnerViewLoaded;
